// --- Core
import PositionHelper, { ALL_POSITION_OPTIONS, positionIsIdp } from './PositionHelper.js';

/**
 * @type string Abbreviation for the flex roster spot that includes
 * QB, RB, WR and TE fantasy positions
 */
export const QB_RB_WR_TE = 'Q|R|W|T';

/**
 * @type string Abbreviation for the flex roster spot that includes
 * RB, WR and TE fantasy positions
 */
const RB_WR_TE = 'R|W|T';

/**
 * @type string Abbreviation for the flex roster spot that includes
 * RB and WR fantasy positions
 */
const RB_WR = 'R|W';

/**
 * @type string Abbreviation for the flex roster spot that includes
 * WR and TE fantasy positions
 */
const WR_TE = 'W|T';

/**
 * @type string Abbreviation for the IDP Flex roster spot that includes
 * DL LB and DB
 */
export const DL_LB_DB = 'D|L|D';

/**
 * @type {string} For the 'bench' roster spot. This an 'inactive' roster spot.
 * an "inactive" roster spot.
 */
export const BN = 'BN';

/**
 * @type {string} For an injured reserve roster spot. This an 'inactive' roster spot.
 */
export const IR = 'IR';

/**
 * @type {string} For the taxi roster spot. This an 'inactive' roster spot.
 */
export const TAXI = 'TAXI';

/**
 * Whether a roster spot is an "active" one which means the player is starting
 * and scoring points in games.
 * @param {string} rosterSpot
 * @returns {boolean}
 */
export const isActiveRosterSpot = rosterSpot => {
    return ![BN, IR, TAXI].includes(rosterSpot) && !isUnsupportedRosterSpot(rosterSpot);
};

/**
 * Whether a roster spot should be ignored by league evaluations.
 * @param {string} rosterSpot
 * @returns {boolean}
 */
export const isUnsupportedRosterSpot = rosterSpot => {
    return !!rosterSpot && !DEFAULT_ORDER.includes(rosterSpot);
};

/**
 * @type array The flex roster spots for offensive fantasy positions
 */
export const OFFENSIVE_FLEX_ROSTER_SPOTS = [QB_RB_WR_TE, RB_WR_TE, RB_WR, WR_TE];

/**
 * @type array The flex roster spots for offensive fantasy positions
 */
const DEFENSIVE_FLEX_ROSTER_SPOTS = [DL_LB_DB];

/**
 * @type {array} Array of all flex roster spot abbreviations
 */
export const ALL_FLEX_ROSTER_SPOTS = OFFENSIVE_FLEX_ROSTER_SPOTS.concat(DEFENSIVE_FLEX_ROSTER_SPOTS);

/**
 * @return array
 */
export const ALL_ROSTER_SPOT_OPTIONS = [
    ...JSON.parse(JSON.stringify(ALL_POSITION_OPTIONS)),
    ...ALL_FLEX_ROSTER_SPOTS.map(rosterSpot => {
        return { value: rosterSpot, label: rosterSpot };
    }),
];

/**
 * @type {array} Default order used when displaying roster spots
 */
export const DEFAULT_ORDER = [
    PositionHelper.QB,
    PositionHelper.TQB,
    PositionHelper.RB,
    PositionHelper.WR,
    PositionHelper.TE,
    QB_RB_WR_TE,
    RB_WR_TE,
    RB_WR,
    WR_TE,
    PositionHelper.K,
    PositionHelper.TK,
    PositionHelper.DEF,
    PositionHelper.DL,
    PositionHelper.LB,
    PositionHelper.DB,
    DL_LB_DB,
    PositionHelper.HC,
    BN,
    IR,
    TAXI,
];

/**
 * Returns true if a roster spot is IDP. Makes use of the same function for
 * checking if a position is IDP with handling for the IDP FLEX roster spot.
 * @param {stromg} rosterSpot
 * @returns {boolean}
 */
export const rosterSpotIsIdp = rosterSpot => {
    return positionIsIdp(rosterSpot) || rosterSpot == DL_LB_DB;
};

/**
 * Sorts an array based on roster spot default order above
 * @param {Array<Object>|Array<string>} rosterSpots Array of roster objects that
 * have a roster spot property or array of strings that are roster spots
 * @return void
 */
export const sortByDefaultRosterSpotOrder = function (rosterSpots, rosterSpotPropertyName = 'rosterSpot') {
    rosterSpots.sort((a, b) => {
        let aVal = 0,
            bVal = 0;
        if (typeof a == 'string') {
            aVal = a;
            bVal = b;
        } else {
            aVal = a[rosterSpotPropertyName];
            bVal = b[rosterSpotPropertyName];
        }
        return DEFAULT_ORDER.indexOf(aVal) - DEFAULT_ORDER.indexOf(bVal);
    });
};

/**
 * Returns an object where the properties are fantasy positions.
 * @param {any} value The value to assign to each property of the object
 * @returns {object}
 */
export const getRosterSpotKeyedObject = value => {
    if (typeof value == 'undefined') {
        value = 0;
    }
    return DEFAULT_ORDER.reduce((acc, item, index) => {
        acc[item] = Array.isArray(value)
            ? [...value] // --- Avoid passing array by reference
            : value;
        return acc;
    }, {});
};

/**
 * This has definitions related to roster spots. Roster spots differ slightly
 * from positions by virtue of the fact that a player has a position but a
 * roster spot exists in the context of a team and/or a league so it has
 * its own helper.
 */
export default {
    // --- All positions are also roster spots so add those as well as our
    // --- FLEX ones that only exist in team / roster context.
    QB: PositionHelper.QB,
    TQB: PositionHelper.TQB,
    RB: PositionHelper.RB,
    WR: PositionHelper.WR,
    TE: PositionHelper.TE,
    QB_RB_WR_TE,
    RB_WR_TE,
    RB_WR,
    WR_TE,
    K: PositionHelper.K,
    TK: PositionHelper.TK,
    DEF: PositionHelper.DEF,
    DL: PositionHelper.DL,
    LB: PositionHelper.LB,
    DB: PositionHelper.DB,
    DL_LB_DB,
    HC: PositionHelper.HC,

    OFFENSIVE_FLEX_ROSTER_SPOTS,
    DEFAULT_ORDER,
    DEFENSIVE_FLEX_ROSTER_SPOTS,
    ALL_FLEX_ROSTER_SPOTS,
};
